import { useState } from 'react';
import { Copy, Check, Code, Terminal } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://coursesearch_api.carlosvargas.workers.dev';

export default function SetupGuide() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const codeExamples = {
    curl: `curl -X POST "${API_URL}/api/v1/search" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "x-domain: yourdomain.com" \\
  -d '{"query": "javascript basics", "limit": 10}'`,

    javascript: `// Using fetch
const response = await fetch('${API_URL}/api/v1/search', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_API_KEY',
    'x-domain': window.location.hostname,
  },
  body: JSON.stringify({
    query: 'javascript basics',
    limit: 10,
  }),
});

const data = await response.json();
console.log(data.results);`,

    widget: `<!-- Option 1: Simple Drop-in Widget -->
<div id="coursesearch"></div>
<script
  src="https://app.mycoursesearch.com/widget.js"
  data-api-key="YOUR_API_KEY"
  data-container="coursesearch">
</script>

<!-- Option 2: Modal Mode (Cmd/Ctrl+K to open) -->
<div id="search-trigger"></div>
<script
  src="https://app.mycoursesearch.com/widget.js"
  data-api-key="YOUR_API_KEY"
  data-container="search-trigger"
  data-mode="modal"
  data-shortcut="k">
</script>

<!-- Option 3: Dark Theme -->
<script
  src="https://app.mycoursesearch.com/widget.js"
  data-api-key="YOUR_API_KEY"
  data-theme="dark"
  data-accent-color="#8b5cf6">
</script>

<!-- Option 4: Programmatic Usage -->
<script src="https://app.mycoursesearch.com/widget.js"></script>
<script>
  const widget = new CourseSearch.CourseSearchWidget({
    apiKey: 'YOUR_API_KEY',
    theme: 'light',
    mode: 'inline',
    placeholder: 'Search courses...',
    onSelect: (result) => {
      console.log('Selected:', result);
    }
  });
  widget.mount('#my-container');
</script>`,

    response: `{
  "results": [
    {
      "id": "abc123",
      "title": "Introduction to JavaScript",
      "description": "Learn the basics of JavaScript programming",
      "url": "https://example.com/courses/js-intro",
      "course": {
        "id": "course123",
        "title": "Web Development 101"
      },
      "relevance": 0.95
    }
  ],
  "total": 1,
  "query": "javascript basics",
  "hasMore": false
}`,
  };

  return (
    <div className="space-y-8">
      {/* Introduction */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Getting Started</h2>
        <p className="text-gray-600 mb-4">
          Follow these steps to integrate CourseSearch into your website:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-gray-600">
          <li>Create an API key in the "API Keys" section</li>
          <li>Add your domain to the "Domains" whitelist</li>
          <li>Add your courses and lessons in the "Courses" section</li>
          <li>Use the API or widget code below to add search to your site</li>
        </ol>
      </div>

      {/* API Endpoint */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Terminal className="w-5 h-5" />
          API Endpoint
        </h3>
        <div className="bg-gray-900 rounded-lg p-4">
          <code className="text-green-400">POST {API_URL}/api/v1/search</code>
        </div>
        <div className="mt-4 space-y-2 text-sm">
          <p className="font-medium text-gray-700">Required Headers:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li><code className="bg-gray-100 px-1 rounded">x-api-key</code> - Your API key</li>
            <li><code className="bg-gray-100 px-1 rounded">x-domain</code> - The domain making the request</li>
            <li><code className="bg-gray-100 px-1 rounded">Content-Type: application/json</code></li>
          </ul>
          <p className="font-medium text-gray-700 mt-4">Request Body:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li><code className="bg-gray-100 px-1 rounded">query</code> (required) - Search term</li>
            <li><code className="bg-gray-100 px-1 rounded">limit</code> (optional) - Max results (default: 10, max: 50)</li>
            <li><code className="bg-gray-100 px-1 rounded">offset</code> (optional) - Pagination offset</li>
          </ul>
        </div>
      </div>

      {/* cURL Example */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Code className="w-5 h-5" />
            cURL Example
          </h3>
          <button
            onClick={() => copyToClipboard(codeExamples.curl, 'curl')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {copiedId === 'curl' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy
          </button>
        </div>
        <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <code className="text-green-400 text-sm">{codeExamples.curl}</code>
        </pre>
      </div>

      {/* JavaScript Example */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Code className="w-5 h-5" />
            JavaScript Example
          </h3>
          <button
            onClick={() => copyToClipboard(codeExamples.javascript, 'js')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {copiedId === 'js' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy
          </button>
        </div>
        <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <code className="text-green-400 text-sm">{codeExamples.javascript}</code>
        </pre>
      </div>

      {/* Widget Example */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Code className="w-5 h-5" />
            Embeddable Widget
          </h3>
          <button
            onClick={() => copyToClipboard(codeExamples.widget, 'widget')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {copiedId === 'widget' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy
          </button>
        </div>
        <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-96">
          <code className="text-green-400 text-sm">{codeExamples.widget}</code>
        </pre>
      </div>

      {/* Response Example */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Code className="w-5 h-5" />
            Response Format
          </h3>
          <button
            onClick={() => copyToClipboard(codeExamples.response, 'response')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {copiedId === 'response' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy
          </button>
        </div>
        <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <code className="text-green-400 text-sm">{codeExamples.response}</code>
        </pre>
      </div>
    </div>
  );
}
